import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Navigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setAuth } from '../../redux/authSlice'
import { RootState } from '../../redux/store'
import i18next, { t } from 'i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding, faEarthAmericas, faEnvelope, faEye, faEyeSlash, faLock, faUser } from '@fortawesome/free-solid-svg-icons'
import { useSnackbar } from 'notistack'
import './login.css'

// Сохранённый язык → язык браузера (2 буквы) → английский. 'ua' (старый код) → 'uk'.
// Валидность против списка 20 языков проверяется после их загрузки (см. эффект ниже).
const detectLanguage = (): string => {
  const saved = localStorage.getItem('language')
  if (saved) return saved === 'ua' ? 'uk' : saved
  const nav = (navigator.language || 'en').slice(0, 2).toLowerCase()
  return nav === 'ua' ? 'uk' : nav
}

const Register: React.FC = () => {
  const [firstName, setFirstName]             = useState('')
  const [lastName, setLastName]               = useState('')
  const [email, setEmail]                     = useState('')
  const [company, setCompany]                 = useState('')
  const [countryId, setCountryId]             = useState('')
  const [countries, setCountries]             = useState<any[]>([])
  const [languages, setLanguages]             = useState<any[]>([])
  const [password, setPassword]               = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [showPass, setShowPass]               = useState(false)
  const [showPassConfirm, setShowPassConfirm] = useState(false)
  const [error, setError]                     = useState<string | null>(null)
  const [loading, setLoading]                 = useState(false)
  const [redirect, setRedirect]               = useState(false)
  const [language, setLanguage]               = useState<string>(detectLanguage)
  const [, forceRender]                       = useState(0)

  const auth     = useSelector((state: RootState) => state.auth.value)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await axios.get('user')
        if (data) dispatch(setAuth(true))
      } catch {
        // auth errors handled by global interceptor
      }
    }
    checkAuth()
  }, [dispatch])

  // Применяем определённый язык и перерисовываемся при смене (t() глобальный)
  useEffect(() => {
    const onChanged = () => forceRender(n => n + 1)
    i18next.on('languageChanged', onChanged)
    i18next.changeLanguage(detectLanguage())
    return () => { i18next.off('languageChanged', onChanged) }
  }, [])

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [c, l] = await Promise.all([
          axios.get('static-country/public/list'),
          axios.get('static-language/public/list'),
        ])
        setCountries(c.data)
        setLanguages(l.data)
      } catch {
        // публичные справочники — ошибки игнорируем
      }
    }
    loadRefs()
  }, [])

  // Когда справочники загружены: автоопределяем страну по региону браузера,
  // язык = язык этой страны (иначе язык браузера если он среди 20, иначе English)
  useEffect(() => {
    if (!countries.length || !languages.length) return
    let cid = countryId
    if (!cid) {
      const region = (navigator.language || '').split('-')[1]?.toUpperCase()
      const m = region && countries.find((c: any) => c.iso_alpha2 === region)
      if (m) { cid = String(m.id); setCountryId(cid) }
    }
    const country = countries.find((c: any) => String(c.id) === String(cid))
    const countryLang = country && languages.find((l: any) => l.id === country.defaultLanguageId)
    const browserValid = languages.some((l: any) => l.iso_639_1 === language)
    changeLng(countryLang ? countryLang.iso_639_1 : browserValid ? language : 'en')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, languages])

  const changeLng = (l: string) => {
    setLanguage(l)
    i18next.changeLanguage(l)
    localStorage.setItem('language', l)
  }

  // Выбор страны → язык подхватывает язык страны (defaultLanguageId)
  const handleCountry = (id: string) => {
    setCountryId(id)
    const country = countries.find((c: any) => String(c.id) === String(id))
    const countryLang =
      country && languages.find((l: any) => l.id === country.defaultLanguageId)
    if (countryLang) changeLng(countryLang.iso_639_1)
  }

  const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/
  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]).{8,}$/

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!firstName || !lastName || !email || !company || !countryId || !password || !passwordConfirm) {
      setError(t('AllFieldsRequired'))
      return
    }
    if (!EMAIL_REGEX.test(email)) {
      setError(t('InvalidEmailFormat'))
      return
    }
    if (!PASSWORD_REGEX.test(password)) {
      setError(t('PasswordRequirements'))
      return
    }
    if (password !== passwordConfirm) {
      setError(t('PasswordsDoNotMatch'))
      return
    }

    setLoading(true)
    try {
      await axios.post('register', {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        password_confirm: passwordConfirm,
        companyid: company,
        staticCountryId: Number(countryId),
        notification_language: language,
      })
      enqueueSnackbar(t('RegistrationSuccess'), { variant: 'success' })
      setRedirect(true)
    } catch (e: any) {
      if (e?.response?.status === 409) setError(t('EmailAlreadyRegistered'))
      else setError(t('RegistrationFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (auth) return <Navigate to='/' />

  if (redirect) return (
    <div className='lp-root'>
      <div className='lp-mesh' />
      <div className='lp-mesh-noise' />
      <div className='lp-page' style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className='lp-form-card' style={{ maxWidth: 460, width: '100%' }}>
          <div className='lp-card-stripe' />
          <div className='lp-card-body' style={{ textAlign: 'center', padding: '48px 40px 40px' }}>

            {/* Envelope icon */}
            <div style={{
              width: 80, height: 80, borderRadius: 24, margin: '0 auto 28px',
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 12px 32px rgba(37,99,235,.35)',
            }}>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="3"/>
                <polyline points="2,4 12,13 22,4"/>
              </svg>
            </div>

            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--lp-txt, #1e293b)', marginBottom: 10, letterSpacing: '-.02em' }}>
              {t('VerifyEmailTitle')}
            </div>

            <div style={{ fontSize: 14, color: 'var(--lp-txt-dim, #64748b)', marginBottom: 6, lineHeight: 1.6 }}>
              {t('VerifyEmailSentTo')}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--lp-accent, #2563eb)', marginBottom: 20, wordBreak: 'break-all' }}>
              {email}
            </div>

            <div style={{
              fontSize: 13.5, color: 'var(--lp-txt-dim, #64748b)',
              lineHeight: 1.65, marginBottom: 32,
              padding: '14px 18px', borderRadius: 12,
              background: 'var(--lp-brd, rgba(0,0,0,.04))',
            }}>
              {t('VerifyEmailDesc')}
            </div>

            <Link to='/login' style={{ textDecoration: 'none', display: 'block' }}>
              <button type='button' className='lp-btn-submit' style={{ width: '100%' }}>
                <span>{t('GoToLogin')}</span>
                <span className='lp-btn-arrow'>→</span>
              </button>
            </Link>

            <div style={{ fontSize: 12, color: 'var(--lp-txt-dim, #94a3b8)', marginTop: 20 }}>
              {t('VerifyEmailSpam')}
            </div>

          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className='lp-root'>
      <div className='lp-mesh' />
      <div className='lp-mesh-noise' />

      {/* Дропдаун выбора языка (все языки) — правый верхний угол */}
      <div style={{ position: 'absolute', top: 20, right: 24, zIndex: 5 }}>
        <select
          value={language}
          onChange={e => changeLng(e.target.value)}
          aria-label='Language'
          style={{
            appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
            cursor: 'pointer', borderRadius: 8,
            padding: '7px 30px 7px 12px', fontSize: 13, fontWeight: 600,
            color: 'var(--lp-txt, #475569)',
            border: '1px solid var(--lp-brd, rgba(0,0,0,.1))',
            backdropFilter: 'blur(8px)',
            background:
              "var(--lp-card, rgba(255,255,255,.85)) url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%2394a3b8' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5 6 6.5 11 1.5'/%3E%3C/svg%3E\") no-repeat right 10px center",
          }}
        >
          {(languages.length
            ? languages
            : [{ id: 'cur', iso_639_1: language || 'en', native_name: (language || 'en').toUpperCase() }]
          ).map((l: any) => (
            <option key={l.id} value={l.iso_639_1}>{l.native_name}</option>
          ))}
        </select>
      </div>

      <div className='lp-page'>

        {/* ══ LEFT PANEL ══ */}
        <div className='lp-left'>
          <div className='lp-left-content'>

            <div className='lp-brand'>
              <div className='lp-brand-mark'>T</div>
              <div>
                <div className='lp-brand-name'>TimeBerlo.com</div>
                <div className='lp-brand-tag'>Portal</div>
              </div>
            </div>

            <div className='lp-hero-title'>
              {t('AuthHeroTitle')}<br />
              <span className='lp-accent'>{t('AuthHeroAccent')}</span>
            </div>
            <div className='lp-hero-sub'>{t('AuthHeroSub')}</div>

            {/* Dashboard mockup */}
            <div className='lp-mockup-wrap'>
              <div className='lp-mockup'>
                <div className='lp-mockup-bar'>
                  <div className='lp-mock-dot' style={{ background: '#ef4444' }} />
                  <div className='lp-mock-dot' style={{ background: '#f59e0b' }} />
                  <div className='lp-mock-dot' style={{ background: '#10b981' }} />
                  <div style={{ flex: 1, marginLeft: 10, height: 16, background: 'var(--lp-brd)', borderRadius: 4, maxWidth: 160, opacity: .5 }} />
                </div>
                <div className='lp-mockup-body'>
                  <div className='lp-mock-row'>
                    {[
                      { label: 'TOIL Balance', val: '+7.5', unit: 'h', color: '#2563eb', w: '62%', grad: '#2563eb,#60a5fa' },
                      { label: 'Approvals',    val: '4',    unit: ' pend.', color: '#d97706', w: '40%', grad: '#d97706,#fbbf24' },
                      { label: 'Logged hrs',   val: '38',   unit: '/40', color: '#059669', w: '95%', grad: '#059669,#34d399' },
                    ].map(s => (
                      <div className='lp-mock-stat' key={s.label}>
                        <div className='lp-stat-label'>{s.label}</div>
                        <div className='lp-stat-val' style={{ color: s.color }}>{s.val}<span className='lp-stat-unit'>{s.unit}</span></div>
                        <div className='lp-bar-wrap'><div className='lp-bar-fill' style={{ width: s.w, background: `linear-gradient(90deg,${s.grad})` }} /></div>
                      </div>
                    ))}
                  </div>
                  <div className='lp-mock-table'>
                    <div className='lp-mock-th'>
                      <div className='lp-mock-th-c'>Employee</div>
                      <div className='lp-mock-th-c'>Week</div>
                      <div className='lp-mock-th-c'>Status</div>
                    </div>
                    {[
                      { init: 'OM', name: 'Oliver M.',  hrs: '38h', st: 'Approved', cls: 'lp-badge-g', bg: '#2563eb,#7c3aed' },
                      { init: 'EH', name: 'Emily H.',   hrs: '40h', st: 'Pending',  cls: 'lp-badge-a', bg: '#059669,#34d399' },
                      { init: 'JP', name: 'James P.',   hrs: '35h', st: 'Approved', cls: 'lp-badge-g', bg: '#db2777,#f472b6' },
                    ].map(r => (
                      <div className='lp-mock-tr' key={r.init}>
                        <div className='lp-mock-td'>
                          <div className='lp-mock-av' style={{ background: `linear-gradient(135deg,${r.bg})` }}>{r.init}</div>
                          {r.name}
                        </div>
                        <div className='lp-mock-num'>{r.hrs}</div>
                        <div className='lp-mock-num'><span className={`lp-mock-badge ${r.cls}`}>{r.st}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className='lp-mockup-float'>
                <div className='lp-float-pill'>
                  <span className='lp-fp-icon'>✅</span>
                  <div><div className='lp-fp-val'>12 reports</div><div className='lp-fp-lbl'>approved today</div></div>
                </div>
                <div className='lp-float-pill'>
                  <span className='lp-fp-icon'>💰</span>
                  <div><div className='lp-fp-val'>£2,340</div><div className='lp-fp-lbl'>expenses saved</div></div>
                </div>
              </div>
            </div>

          </div>
          <div className='lp-left-stripe' />
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div className='lp-right'>
          <div className='lp-form-card'>
            <div className='lp-card-stripe' />
            <div className='lp-card-body lp-compact'>

              <div className='lp-greeting'>{t('CreateAccount')} 👋</div>
              <div className='lp-greeting-sub'>{t('CreateAccountSub')}</div>

              {error && <div className='lp-err-banner'>{error}</div>}

              <form onSubmit={handleRegister}>

                {/* First name */}
                <div className='lp-field'>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faUser} /></span>
                    <input
                      className='lp-inp'
                      type='text'
                      placeholder={t('FirstName')}
                      autoComplete='given-name'
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Last name */}
                <div className='lp-field'>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faUser} /></span>
                    <input
                      className='lp-inp'
                      type='text'
                      placeholder={t('LastName')}
                      autoComplete='family-name'
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className='lp-field'>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faEnvelope} /></span>
                    <input
                      className='lp-inp'
                      type='email'
                      placeholder={t('Email')}
                      autoComplete='email'
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                {/* Company */}
                <div className='lp-field'>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faBuilding} /></span>
                    <input
                      className='lp-inp'
                      type='text'
                      placeholder={t('CompanyName')}
                      autoComplete='organization'
                      value={company}
                      onChange={e => setCompany(e.target.value)}
                    />
                  </div>
                </div>

                {/* Country */}
                <div className='lp-field'>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faEarthAmericas} /></span>
                    <select
                      className='lp-inp'
                      value={countryId}
                      onChange={e => handleCountry(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    >
                      <option value=''>{t('SelectCountry')}</option>
                      {countries.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.flag_emoji ? `${c.flag_emoji}  ` : ''}{c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Password */}
                <div className='lp-field'>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faLock} /></span>
                    <input
                      className='lp-inp'
                      type={showPass ? 'text' : 'password'}
                      placeholder={t('Password')}
                      autoComplete='new-password'
                      style={{ paddingRight: '44px' }}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                    />
                    <button type='button' className='lp-inp-eye' aria-label={t('Password')} onClick={() => setShowPass(p => !p)}>
                      <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </div>

                {/* Confirm password */}
                <div className='lp-field'>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faLock} /></span>
                    <input
                      className='lp-inp'
                      type={showPassConfirm ? 'text' : 'password'}
                      placeholder={t('ConfirmPassword')}
                      autoComplete='new-password'
                      style={{ paddingRight: '44px' }}
                      value={passwordConfirm}
                      onChange={e => setPasswordConfirm(e.target.value)}
                    />
                    <button type='button' className='lp-inp-eye' aria-label={t('ConfirmPassword')} onClick={() => setShowPassConfirm(p => !p)}>
                      <FontAwesomeIcon icon={showPassConfirm ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </div>

                <button type='submit' className='lp-btn-submit' disabled={loading}>
                  {loading
                    ? <><div className='lp-spinner' /><span>{t('SignUp')}…</span></>
                    : <><span>{t('SignUp')}</span><span className='lp-btn-arrow'>→</span></>
                  }
                </button>

              </form>

              <div className='lp-remember-row' style={{ justifyContent: 'center', marginTop: 22 }}>
                <div className='lp-remember-lbl'>
                  {t('AlreadyRegistered')}?{' '}
                  <Link to='/login' style={{ color: 'var(--lp-accent, #2563eb)', fontWeight: 600 }}>{t('SignIn')}</Link>
                </div>
              </div>

            </div>
          </div>

          <div className='lp-trust-row'>
            <div className='lp-trust-item'>🔒 <span>{t('TrustSsl')}</span></div>
            <div className='lp-trust-dot' />
            <div className='lp-trust-item'>🛡 <span>{t('TrustGdpr')}</span></div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default Register

/*
  ── OLD REGISTER UI (удалить после подтверждения дизайна) ─────────────
  (!auth ? (
    <div className='h-[60vh] w-full flex justify-center items-center'>
      <div className="max-w-[400px] m-auto flex flex-col justify-start px-[20px] pt-[60px] rounded-[5px] form-box w-full relative">
        <div className='flex justify-between items-center absolute bg-[var(--bg-top)] top-0 w-full left-0 px-[20px] rounded-t-[5px]'>
          <div className="flex gap-4 my-[7px] justify-start items-center w-[80%]">
            <h3 className='text-[18px]'>{t('SignUp')}:</h3>
          </div>
        </div>
        {error && <div className='...'>{error}</div>}
        First Name / Last Name / Email / Company / Password / Confirm Password inputs (Tailwind form-box style)
        <Button variant="contained" onClick={handleClick}>{t('SignUp')}</Button>
        <p>{t('AlreadyRegistered')}? <Link to="/login">{t('SignIn')}</Link></p>
      </div>
    </div>
  ) : <Navigate to='/' />)
  ──────────────────────────────────────────────────────────────────────
*/
